import { Box, Flex } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import { useState } from 'react'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'

import Select from '@/components/UI/Select'
import Tippy from '@/components/UI/Tippy'

import { DEFAULT_PAGE_NUMBER_QUERY_KEY } from './constants'
import { sanitizeQueryValue } from './helpers'
import type { Option, SortWidgetProps } from './types'

/**
 * @deprecated Use useSortWidgetV2()
 */
export function SortWidget<T>({
  sorts,
  queryKey = 'sort',
  zIndex = 'auto',
  width = '15rem',
  text = 'Sort by',
  defaultSortKey,
  menuPortalTargetRef,
  tooltipText,
}: SortWidgetProps<T>): JSX.Element {
  const router = useRouter()
  const queryValue = `${router.query[queryKey]}`
  const options: Option[] = Object.keys(sorts).map(key => ({
    label: key,
    inputValue: key,
    value: sanitizeQueryValue(key),
  }))
  const optionMatchingQuery = options.find(item => item.value === queryValue)
  const optionDefault = options.find(item => item.value === defaultSortKey)
  const optionFallback = options?.[0]
  const [optionOptimistic, setOptionOptimistic] = useState<Option | null>(null)
  const option = optionOptimistic ?? optionMatchingQuery ?? optionDefault ?? optionFallback

  const currentPageId = router.query[DEFAULT_PAGE_NUMBER_QUERY_KEY]

  const [handleSortChange] = useAPIWrapper(
    async (newOption?: Option | null) => {
      if (newOption == null) {
        return
      }
      // Optimistic update
      setOptionOptimistic(newOption)
      const pageReset = currentPageId != null ? { [DEFAULT_PAGE_NUMBER_QUERY_KEY]: 1 } : {}

      await router.replace({
        query: {
          ...router.query,
          [queryKey]: newOption.value,
          ...pageReset,
        },
      })

      // Revert optimistic update to let the query stuff take precedence
      setOptionOptimistic(null)
    },
    [currentPageId, queryKey, router],
    'Process Sort Value change'
  )

  return (
    <Flex align="center">
      <Tippy content={tooltipText} disabled={tooltipText == null}>
        <Box fontWeight="bold" mr={2} flexShrink={0}>
          {text}
        </Box>
      </Tippy>
      <Box width={width} zIndex={zIndex}>
        <Select<Option>
          instanceId={queryKey}
          name={queryKey}
          options={options}
          value={option}
          onChange={handleSortChange}
          isSearchable
          isClearable={false}
          components={{
            IndicatorSeparator: () => null,
          }}
          menuPortalTarget={menuPortalTargetRef?.current}
        />
      </Box>
    </Flex>
  )
}

/**
 * @deprecated Use useSortWidgetV2()
 */
export function useSort<T>(sorts: Record<string, T>, queryKey = 'sort'): T {
  const router = useRouter()
  const sort = `${router.query[queryKey]}`
  const match = Object.entries(sorts).find(([key]) => sanitizeQueryValue(key) === sort)
  if (match) {
    return match[1]
  }
  return Object.values(sorts)[0]
}
